import { inject } from '@angular/core';
import { OAuthService } from '@olameter/o-authentication';
import { ONotificationService } from '@olameter/o-utilities';
import { OConfigService } from '@olameter/o-config-service';
import { TranslateService } from '@ngx-translate/core';
import { Empty } from 'google-protobuf/google/protobuf/empty_pb';
import { from, Observable, switchMap } from 'rxjs';

interface HealthzCapable {
  healthz(request: Empty, callback: (error: any, response: any) => void): void;
}

export abstract class BaseGRRCService<T extends HealthzCapable> {
  protected client: T;
  private config = inject(OConfigService);
  private authService = inject(OAuthService);
  private translateService = inject(TranslateService);
  protected notification = inject(ONotificationService);

  constructor(clientCtor: new (apiUrl: string) => T) {
    this.client = new clientCtor(this.config.getConfig().apiUrl);
  }

  protected handleError(error: any, message?: string): void {
    this.authService.processError(error);
    if (message) {
      this.notification.showError(message);
    } else {
      this.notification.showError(
        error?.message || this.translate('NOTIFICATIONS.ERRORS.UNEXPECTED')
      );
    }
    console.error('Service request failed:', error);
  }

  protected translate(
    key: string,
    params?: Record<string, string | number>
  ): string {
    return this.translateService.instant(key, params);
  }

  protected showTranslatedSuccess(
    key: string,
    params?: Record<string, string | number>
  ): void {
    this.notification.showSuccess(this.translate(key, params));
  }

  protected showConnectionError(serviceKey: string, reason?: unknown): void {
    const details = reason ? ` ${JSON.stringify(reason)}` : '';
    this.notification.showError(
      this.translate('NOTIFICATIONS.ERRORS.SERVICE_UNAVAILABLE', {
        service: this.translate(serviceKey),
        details,
      })
    );
  }

  protected getGrpcRequestOptions(): Promise<any> {
    return this.authService.getGrpcAuthorizationRequestOption();
  }
  protected withGrpcOptions<R>(
    handler: (requestOptions: any) => Observable<R>
  ): Observable<R> {
    return from(this.getGrpcRequestOptions()).pipe(
      switchMap((requestOptions) => handler(requestOptions))
    );
  }

  protected getApplicationBEVersion() {
    return new Promise((resolve, reject) => {
      const request = new Empty();
      this.client.healthz(request, (error: any, response: any) => {
        if (error) {
          reject(error);
        } else {
          const result = response?.toArray();
          if (result?.length > 0) {
            resolve(result[1]);
          } else {
            resolve(null);
          }
        }
      });
    });
  }
}
